import React, { useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { flagDefinitionsApi } from '../../api/flagSystemApi';
import { flagError } from '../../utils/flagSystem';
import {
  FLAG_IMPORT_MAX_BYTES, FLAG_IMPORT_MAX_ROWS, FLAG_IMPORT_TEMPLATE, parseFlagImportFile, validateFlagImportCsv,
} from '../../utils/flagCsvImport';

const fileSize = (bytes) => `${(bytes / 1024).toFixed(bytes < 10240 ? 1 : 0)} KiB`;
const importError = (failure) => {
  const status = failure && failure.response && failure.response.status;
  if (status === 409) return 'The import was rejected because a concurrent or duplicate change occurred. Review the current flags, then retry explicitly if appropriate.';
  if (status === 413) return 'The file or request is too large. Choose a CSV no larger than 1 MiB.';
  if (!failure || !failure.response) return 'The import may have completed, but the result is uncertain. Check current assignments before retrying; retrying skips existing active assignments.';
  return flagError(failure, 'The import could not be completed. Review the CSV and try again.');
};

const FlagCsvImportDialog = ({ customerId, flags, flagsAvailable, onAccessError, onImported }) => {
  const [visible, setVisible] = useState(false);
  const [file, setFile] = useState(null);
  const [validation, setValidation] = useState({ records: [], errors: [], warnings: [] });
  const [acknowledged, setAcknowledged] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [result, setResult] = useState(null);
  const [importCustomerId, setImportCustomerId] = useState(null);
  const fileInput = useRef(null);

  const close = () => {
    if (!uploading) {
      setVisible(false); setFile(null); setValidation({ records: [], errors: [], warnings: [] });
      setAcknowledged(false); setUploading(false); setRequestError(''); setResult(null); setImportCustomerId(null);
    }
  };
  const open = () => {
    setVisible(true); setFile(null); setValidation({ records: [], errors: [], warnings: [] });
    setAcknowledged(false); setUploading(false); setRequestError(''); setResult(null); setImportCustomerId(customerId);
  };
  const selectFile = async (event) => {
    const selected = event.target.files && event.target.files[0];
    setFile(selected || null); setAcknowledged(false); setRequestError(''); setResult(null);
    if (!selected) {
      setValidation({ records: [], errors: [], warnings: [] });
      return;
    }
    if (selected.size > FLAG_IMPORT_MAX_BYTES) {
      setValidation({ records: [], errors: [{ rowNumber: null, message: 'The CSV must be no larger than 1 MiB.' }], warnings: [] });
      return;
    }
    try {
      setValidation(validateFlagImportCsv(await parseFlagImportFile(selected), flagsAvailable ? flags : null));
    } catch (failure) {
      setValidation({ records: [], errors: [{ rowNumber: null, message: failure.message || 'The CSV could not be read.' }], warnings: [] });
    }
  };
  const downloadTemplate = () => {
    const href = URL.createObjectURL(new Blob([FLAG_IMPORT_TEMPLATE], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = href; link.download = 'member-flag-import-template.csv';
    document.body.appendChild(link); link.click(); link.remove();
    window.setTimeout(() => URL.revokeObjectURL(href), 0);
  };
  const upload = async () => {
    if (uploading || !file || !acknowledged || validation.errors.length || importCustomerId !== customerId) return;
    setUploading(true); setRequestError('');
    try {
      const saved = await flagDefinitionsApi.importCsv(importCustomerId, file);
      setResult(saved); onImported();
    } catch (failure) {
      onAccessError(failure);
      setRequestError(importError(failure));
    } finally { setUploading(false); }
  };
  const canUpload = Boolean(file && validation.records.length && !validation.errors.length && acknowledged && !result &&
    importCustomerId === customerId);

  return <>
    <Button label="Import CSV" icon="pi pi-upload" className="p-button-secondary" onClick={open} />
    <Dialog header="Import member flags from CSV" visible={visible} className="flag-editor flag-csv-import-dialog"
      draggable={false} resizable={false} modal closable={!uploading} onHide={close}
      footer={<div className="flex justify-content-center flex-wrap gap-3">
        <Button label={result ? 'Close' : 'Cancel'} className="p-button-text" disabled={uploading} onClick={close} />
        {!result && <Button label="Import CSV" icon="pi pi-upload" className="p-button-success" loading={uploading}
          disabled={!canUpload || uploading} onClick={upload} />}
      </div>}>
      <p className="flag-csv-import-lead">Assignments apply across all locations for customer {importCustomerId}. No location is selected or sent.</p>
      <div className="flag-csv-import-instructions">
        <div className="flag-csv-import-instructions-header">
          <div><strong>Required columns:</strong> <code>user_id,flag_code,flag_name</code></div>
          <Button label="Download CSV template" icon="pi pi-download" className="p-button-text p-button-sm" onClick={downloadTemplate} />
        </div>
        <ul>
          <li><code>user_id</code>: membership number used in the Active Access app for access control.</li>
          <li><code>flag_code</code>: unique flag reference code.</li>
          <li><code>flag_name</code>: if the flag code does not exist, a flag is created with this name and the default presets.</li>
          <li>Maximum file size: 1 MiB. Maximum data rows: {FLAG_IMPORT_MAX_ROWS}.</li>
        </ul>
      </div>
      <section className="flag-csv-file-picker" aria-labelledby="flag-csv-file-heading">
        <div>
          <strong id="flag-csv-file-heading">CSV file</strong>
          <span>Upload the original CSV file. It is not modified before import.</span>
        </div>
        <div className="flag-csv-file-picker-action">
          <input ref={fileInput} id="flag-csv-file" type="file" accept=".csv,text/csv" disabled={uploading} onChange={selectFile} />
          <Button label="Choose CSV file" icon="pi pi-folder-open" className="p-button-outlined" disabled={uploading}
            onClick={() => fileInput.current.click()} />
          <span className={file ? 'flag-csv-file-selected' : 'flag-csv-file-empty'}>
            {file ? `${file.name} (${fileSize(file.size)})` : 'No file selected'}
          </span>
        </div>
      </section>
      {requestError && <Message severity="error" text={requestError} className="mt-3" />}
      {validation.errors.length > 0 && <div className="mt-3"><Message severity="error" text="Correct the CSV before importing." />
        <ul className="flag-csv-issues">{validation.errors.map((error, index) => <li key={`${error.rowNumber}-${index}`}>
          {error.rowNumber ? `Record ${error.rowNumber}: ` : ''}{error.message}</li>)}</ul></div>}
      {validation.warnings.map((warning, index) => <Message key={index} severity="warn" text={warning} className="mt-2" />)}
      {validation.records.length > 0 && <section className="mt-3" aria-labelledby="flag-csv-preview-heading">
        <h5 id="flag-csv-preview-heading">Parsed record preview</h5>
        <DataTable value={validation.records} responsiveLayout="stack" breakpoint="700px" size="small">
          <Column field="rowNumber" header="Record" />
          <Column field="memberId" header="Member ID" />
          <Column field="code" header="Flag code" />
          <Column field="name" header="Flag name" />
        </DataTable>
      </section>}
      {file && !result && <section className="flag-csv-confirmation mt-4">
        <div className="flex align-items-start gap-2">
          <Checkbox inputId="flag-csv-acknowledgment" checked={acknowledged} disabled={uploading || Boolean(validation.errors.length)}
            onChange={event => setAcknowledged(event.checked)} />
          <label htmlFor="flag-csv-acknowledgment">
            Imported assignments apply across all locations for this customer. Missing flag codes will create active flags that block entry and exit at all times until their configuration is changed.
          </label>
        </div>
        <ul>
          <li>Existing flag names and configurations are not changed.</li>
          <li>Disabled existing flags cause the import to fail; they are not automatically reactivated.</li>
          <li>Existing active assignments are skipped.</li>
          <li>New flags initially disable site-role overrides and assignment/removal permissions.</li>
          <li>“Provisional” does not mean automatic expiration.</li>
          <li>Unknown member IDs can be registered in the flag-member registry; normal membership accounts are not created.</li>
          <li>Importing a previously location-bound flag member promotes its registry record to customer-wide scope, making existing flag assignments visible across customer locations.</li>
          <li>The import is atomic: a failed import does not partially apply.</li>
        </ul>
      </section>}
      {result && <section className="mt-4" aria-labelledby="flag-csv-results-heading">
        <Message severity="success" text="Import completed. Review imported flags, especially when new flags were created." />
        <h5 id="flag-csv-results-heading" className="mt-3">Import results</h5>
        <dl className="flag-csv-results-summary">
          <dt>Total rows</dt><dd>{result.totalRows}</dd><dt>Flags created</dt><dd>{result.flagsCreated}</dd>
          <dt>Members registered</dt><dd>{result.membersRegistered}</dd><dt>Members promoted to customer-wide scope</dt><dd>{result.membersPromoted}</dd>
          <dt>Assignments created</dt><dd>{result.assignmentsCreated}</dd><dt>Assignments skipped</dt><dd>{result.assignmentsSkipped}</dd>
        </dl>
        <DataTable value={result.rows || []} responsiveLayout="stack" breakpoint="700px" size="small">
          <Column field="rowNumber" header="Record" /><Column field="memberId" header="Member ID" />
          <Column field="code" header="Flag code" /><Column field="status" header="Status" />
        </DataTable>
      </section>}
    </Dialog>
  </>;
};

export default FlagCsvImportDialog;
